type GenericRecord = Record<string, unknown>;

export type FormSectionId =
  | 'applicant'
  | 'agent'
  | 'site'
  | 'proposal'
  | 'development'
  | 'ownership'
  | 'access'
  | 'environment'
  | 'heritage'
  | 'materials'
  | 'dates'
  | 'fees'
  | 'documents'
  | 'other';

export interface NormalizedFormField {
  key: string;
  field_key: string | null;
  label: string;
  question: string;
  question_contextual: string;
  value: string;
  answer_raw: string;
  answer_normalized: string;
  group: FormSectionId;
  group_id: string | null;
  context_id: string | null;
  context_label: string | null;
  context_id_raw: string | null;
  context_label_raw: string | null;
  confidence: number | null;
  page: number | null;
  is_checkbox_option: boolean;
  is_checked: boolean | null;
  searchText: string;
  raw: GenericRecord;
}

export interface NormalizedFormPayload {
  meta: {
    version: string | number | null;
    parsedAt: string | null;
  };
  parsed: unknown;
  fields: NormalizedFormField[];
}

export interface FormDataSummary {
  totalFields: number;
  filledFields: number;
  missingFields: number;
  coverage: number;
  sectionsCount: number;
  contextualFields: number;
  contextualCoverage: number;
  keyedFields: number;
  keyedCoverage: number;
  checkboxFields: number;
  checkedCheckboxes: number;
  avgConfidence: number | null;
  highConfidenceRate: number | null;
}

const INLINE_FORM_KEYS = [
  'application_form',
  'applicationForm',
  'application_form_data',
  'application_form_json',
  'application_form_parsed',
  'form_data',
  'form',
  'form_parsed',
  'parsed_form',
  'parsed',
  'parsed_json',
  'parsed_fields',
  'parsed_data'
];

const PARSED_PAYLOAD_KEYS = [
  'parsed',
  'parsed_json',
  'parsed_fields',
  'parsed_data',
  'form_data',
  'fields',
  'form_fields',
  'extracted_fields',
  'items',
  'data'
];

const FIELD_ARRAY_KEYS = [
  'fields',
  'form_fields',
  'extracted_fields',
  'parsed_data',
  'parsed_fields',
  'items'
];

const CONTEXT_ALIASES: Record<string, string> = {
  applicant: 'applicant_details',
  applicant_details: 'applicant_details',
  agent: 'agent_details',
  agent_details: 'agent_details',
  site_address_details: 'site_address',
  site_address: 'site_address',
  ownership_certificate: 'ownership_details',
  land_ownership_certificate: 'ownership_details',
  interest_in_the_land: 'ownership_details',
  ownership_details: 'ownership_details',
  declare_listed_building_consent: 'declaration',
  declaration: 'declaration',
  payment_fee: 'fees',
  payment_details: 'fees'
};

const SECTION_KEYWORDS: Array<{ section: FormSectionId; pattern: RegExp }> = [
  { section: 'applicant', pattern: /applicant/ },
  { section: 'agent', pattern: /agent|consultant/ },
  { section: 'site', pattern: /site|location|address|postcode|uprn|grid|northing|easting/ },
  { section: 'proposal', pattern: /proposal|description of development|works|summary/ },
  { section: 'development', pattern: /development|project|scheme|use class/ },
  { section: 'ownership', pattern: /ownership|owner|certificate|interest in the land/ },
  { section: 'access', pattern: /access|parking|transport|traffic|road|vehicle/ },
  { section: 'environment', pattern: /environment|drainage|flood|tree|ecology|biodiversity|contamination/ },
  { section: 'heritage', pattern: /heritage|listed|conservation|archaeology/ },
  { section: 'materials', pattern: /material|appearance|design/ },
  { section: 'dates', pattern: /date|received|validated|declaration|signed/ },
  { section: 'fees', pattern: /fee|payment|charge/ },
  { section: 'documents', pattern: /document|drawing|plan|statement|report/ }
];

const QA_WARNING_VALUE_PATTERN = /^(?:low_confidence|conflict):/i;
const INTERNAL_PARSER_ROOT_KEYS = new Set([
  '_all_fields',
  '_groups',
  '_parser',
  'field_confidence',
  'field_sources',
  'qa_details',
  'withheld_fields',
  'withheld_field_reasons'
]);

const isQaWarningFieldPath = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  return value.trim().toLowerCase().includes('qa_warnings');
};

const getRootPathSegment = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const normalized = trimmed.replace(/^\[+/, '');
  const match = normalized.match(/^[^.[]+/);
  return match ? match[0].toLowerCase() : '';
};

const isInternalParserPath = (value: unknown): boolean => {
  const root = getRootPathSegment(value);
  if (!root) return false;
  if (root.startsWith('_')) return true;
  return INTERNAL_PARSER_ROOT_KEYS.has(root);
};

const toSearchText = (value: unknown): string => String(value ?? '').toLowerCase();

const firstNonEmptyString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return '';
};

const normalizeLabel = (value: string): string => {
  if (!value) return '';
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '_');

const parsePossibleJson = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const normalizeConfidence = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return null;
  const percent = numeric <= 1 ? numeric * 100 : numeric;
  return Math.min(100, Math.max(0, Math.round(percent)));
};

const toNullableBoolean = (value: unknown): boolean | null => {
  if (value === true || value === false) return value;
  if (value === null || value === undefined || value === '') return null;
  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return null;
};

const looksLikeFieldItem = (value: unknown): value is GenericRecord => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const obj = value as GenericRecord;
  return (
    'question' in obj ||
    'question_contextual' in obj ||
    'answer_raw' in obj ||
    'answer_normalized' in obj ||
    'answer' in obj ||
    'value' in obj ||
    'field_key' in obj ||
    'is_checkbox_option' in obj
  );
};

const looksLikeApplicationRecord = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const obj = value as GenericRecord;
  const hasAppIdentity =
    typeof obj.reference_no === 'string' ||
    typeof obj.ref === 'string' ||
    typeof obj.address === 'string' ||
    typeof obj.location === 'string';
  if (!hasAppIdentity) return false;
  return PARSED_PAYLOAD_KEYS.every((key) => obj[key] === undefined);
};

const canonicalizeContextId = (value: unknown): string | null => {
  const raw = firstNonEmptyString(value);
  if (!raw) return null;
  const slug = slugify(raw);
  if (!slug) return null;
  return CONTEXT_ALIASES[slug] || slug;
};

const inferSectionId = (parts: Array<string | null | undefined>): FormSectionId => {
  const text = parts.filter(Boolean).join(' ').toLowerCase();
  if (!text) return 'other';
  for (const entry of SECTION_KEYWORDS) {
    if (entry.pattern.test(text)) return entry.section;
  }
  return 'other';
};

const parseFieldCandidates = (parsed: unknown, sectionHint?: string): Array<GenericRecord | unknown> => {
  if (!parsed) return [];
  if (Array.isArray(parsed)) return parsed;
  if (typeof parsed !== 'object') return [parsed];
  const obj = parsed as GenericRecord;

  for (const key of FIELD_ARRAY_KEYS) {
    const candidate = parsePossibleJson(obj[key]);
    if (Array.isArray(candidate)) return candidate;
  }

  const sections = parsePossibleJson(obj.sections);
  if (Array.isArray(sections)) {
    return sections.flatMap((section: unknown) => {
      if (!section || typeof section !== 'object') return [];
      const sectionObj = section as GenericRecord;
      const nestedHint = firstNonEmptyString(
        sectionObj.id,
        sectionObj.group,
        sectionObj.group_id,
        sectionObj.title,
        sectionObj.label,
        sectionHint
      );
      for (const key of FIELD_ARRAY_KEYS) {
        const candidate = parsePossibleJson(sectionObj[key]);
        if (Array.isArray(candidate)) {
          return candidate.map((item) => {
            if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
            return { ...item, _section_hint: nestedHint } as GenericRecord;
          });
        }
      }
      return [];
    });
  }

  return [obj];
};

const flattenLegacyObject = (value: unknown, prefix = ''): GenericRecord[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value.flatMap((entry, index) => flattenLegacyObject(entry, `${prefix}[${index}]`));
  }
  if (typeof value !== 'object') {
    const key = prefix || 'value';
    return [{ key, question: normalizeLabel(key), answer_normalized: String(value), answer_raw: String(value) }];
  }
  const obj = value as GenericRecord;
  return Object.entries(obj).flatMap(([rawKey, nested]) => {
    const key = prefix ? `${prefix}.${rawKey}` : rawKey;
    if (isQaWarningFieldPath(rawKey) || isQaWarningFieldPath(key)) {
      return [];
    }
    if (isInternalParserPath(rawKey) || isInternalParserPath(key)) {
      return [];
    }
    if (nested !== null && typeof nested === 'object') {
      return flattenLegacyObject(nested, key);
    }
    const raw = nested === null || nested === undefined ? '' : String(nested);
    return [{ key, question: normalizeLabel(rawKey), answer_normalized: raw, answer_raw: raw }];
  });
};

const buildStableFieldKey = (item: GenericRecord, fallbackLabel: string, index: number): string => {
  const explicit = firstNonEmptyString(item.field_key, item.fieldKey);
  if (explicit) return explicit;

  const keyBase = firstNonEmptyString(item.key, item.id);
  if (keyBase) return slugify(keyBase);

  const sectionPart = firstNonEmptyString(
    canonicalizeContextId(item.context_id ?? item.contextId),
    firstNonEmptyString(item.group_id, item.groupId, item.group)
  );
  const labelPart = slugify(fallbackLabel || 'field');
  const page = Number.isFinite(Number(item.page)) ? `p${Number(item.page)}` : '';
  return [sectionPart, labelPart, page, String(index + 1)].filter(Boolean).join('.');
};

const normalizeField = (entry: unknown, index: number): NormalizedFormField | null => {
  if (entry === null || entry === undefined) return null;

  const item: GenericRecord = looksLikeFieldItem(entry)
    ? (entry as GenericRecord)
    : (flattenLegacyObject(entry)[0] as GenericRecord);

  const question = firstNonEmptyString(item.question, item.label, item.field, item.name, item.key, item.id);
  const questionContextual = firstNonEmptyString(item.question_contextual, item.questionContextual);
  const label = questionContextual || question || `Field ${index + 1}`;
  const answerNormalized = firstNonEmptyString(item.answer_normalized, item.answerNormalized);
  const answerRaw = firstNonEmptyString(
    item.answer_raw,
    item.answerRaw,
    item.answer,
    item.value,
    item.response,
    item.text,
    item.content,
    item.data,
    item.result
  );
  const value = answerNormalized || answerRaw;
  const isCheckboxOption = Boolean(item.is_checkbox_option ?? item.isCheckboxOption);
  const isChecked = toNullableBoolean(item.is_checked ?? item.isChecked);

  if (isCheckboxOption && isChecked === false) {
    return null;
  }

  const contextIdRaw = firstNonEmptyString(item.context_id, item.contextId) || null;
  const contextLabelRaw = firstNonEmptyString(item.context_label, item.contextLabel) || null;
  const canonicalContextId = canonicalizeContextId(contextIdRaw);
  const groupId = firstNonEmptyString(item.group_id, item.groupId, item.group, item.section, item.category) || null;
  const fieldKey = buildStableFieldKey(item, label, index);
  if (
    isQaWarningFieldPath(fieldKey) ||
    isQaWarningFieldPath(question) ||
    isQaWarningFieldPath(questionContextual)
  ) {
    return null;
  }
  if (isInternalParserPath(fieldKey)) {
    return null;
  }
  if (value && QA_WARNING_VALUE_PATTERN.test(value.trim()) && /qa\s*warnings?/i.test(label)) {
    return null;
  }
  const keyContext = canonicalContextId || (fieldKey.includes('.') ? fieldKey.split('.')[0] : '');
  const contextId = canonicalizeContextId(keyContext) || null;
  const contextLabel = firstNonEmptyString(contextLabelRaw, normalizeLabel(contextId || '')) || null;
  const section = inferSectionId([contextId, groupId, fieldKey, label, String(item._section_hint || '')]);
  const confidence = normalizeConfidence(item.confidence ?? item.score ?? item.confidence_score);
  const page = Number.isFinite(Number(item.page)) ? Number(item.page) : null;

  const searchText = [
    label,
    question,
    questionContextual,
    value,
    fieldKey,
    contextId ?? '',
    contextLabel ?? '',
    groupId ?? ''
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return {
    key: `${fieldKey}#${index + 1}`,
    field_key: fieldKey || null,
    label: normalizeLabel(label),
    question,
    question_contextual: questionContextual,
    value,
    answer_raw: answerRaw,
    answer_normalized: answerNormalized,
    group: section,
    group_id: groupId,
    context_id: contextId,
    context_label: contextLabel,
    context_id_raw: contextIdRaw,
    context_label_raw: contextLabelRaw,
    confidence,
    page,
    is_checkbox_option: isCheckboxOption,
    is_checked: isChecked,
    searchText,
    raw: item
  };
};

export const extractInlineApplicationFormData = (application: GenericRecord | null | undefined): unknown => {
  if (!application || typeof application !== 'object') return null;
  for (const key of INLINE_FORM_KEYS) {
    if (application[key] !== undefined && application[key] !== null) {
      return application[key];
    }
  }
  return null;
};

const unwrapPayload = (payload: unknown): unknown => {
  const parsed = parsePossibleJson(payload);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return parsed;
  const obj = parsed as GenericRecord;
  if (obj.data !== undefined) return parsePossibleJson(obj.data);
  if (obj.results !== undefined) return parsePossibleJson(obj.results);
  if (obj.items !== undefined && Array.isArray(obj.items)) return obj.items;
  return parsed;
};

const extractPreNormalizedFields = (payload: unknown): unknown[] => {
  const unwrapped = unwrapPayload(payload);
  if (!unwrapped || typeof unwrapped !== 'object' || Array.isArray(unwrapped)) return [];
  const obj = unwrapped as GenericRecord;

  const direct = parsePossibleJson(obj.normalized_fields);
  if (Array.isArray(direct)) return direct;

  const camel = parsePossibleJson(obj.normalizedFields);
  if (Array.isArray(camel)) return camel;

  const nestedData = parsePossibleJson(obj.data);
  if (nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
    const nestedObj = nestedData as GenericRecord;
    const nested = parsePossibleJson(nestedObj.normalized_fields);
    if (Array.isArray(nested)) return nested;
    const nestedCamel = parsePossibleJson(nestedObj.normalizedFields);
    if (Array.isArray(nestedCamel)) return nestedCamel;
  }

  return [];
};

const extractParsedPayload = (payload: unknown): unknown => {
  const unwrapped = unwrapPayload(payload);
  if (!unwrapped) return null;
  if (Array.isArray(unwrapped)) return unwrapped;
  if (typeof unwrapped !== 'object') return parsePossibleJson(unwrapped);
  const obj = unwrapped as GenericRecord;

  if (looksLikeApplicationRecord(obj)) {
    const inline = extractInlineApplicationFormData(obj);
    return inline ? extractParsedPayload(inline) : null;
  }

  if (obj.application_form !== undefined) {
    const nested = extractParsedPayload(obj.application_form);
    if (nested) return nested;
  }

  for (const key of PARSED_PAYLOAD_KEYS) {
    const candidate = parsePossibleJson(obj[key]);
    if (candidate !== undefined && candidate !== null) return candidate;
  }

  return obj;
};

export const normalizeFormPayload = (payload: unknown): NormalizedFormPayload => {
  if (!payload) {
    return {
      meta: { version: null, parsedAt: null },
      parsed: null,
      fields: []
    };
  }

  const envelope = unwrapPayload(payload);
  const sourceObj = envelope && typeof envelope === 'object' && !Array.isArray(envelope)
    ? (envelope as GenericRecord)
    : (payload as GenericRecord);
  const parsed = extractParsedPayload(payload);
  const preNormalizedCandidates = extractPreNormalizedFields(payload);

  const fields = preNormalizedCandidates.length > 0
    ? preNormalizedCandidates
        .map((candidate, index) => normalizeField(candidate, index))
        .filter((field): field is NormalizedFormField => Boolean(field))
    : parseFieldCandidates(parsed)
        .flatMap((candidate) => {
          if (looksLikeFieldItem(candidate)) return [candidate];
          return flattenLegacyObject(candidate);
        })
        .map((candidate, index) => normalizeField(candidate, index))
        .filter((field): field is NormalizedFormField => Boolean(field));

  return {
    meta: {
      version:
        (sourceObj?.extractor_version as string | number | undefined) ??
        (sourceObj?.extractorVersion as string | number | undefined) ??
        (sourceObj?.version as string | number | undefined) ??
        null,
      parsedAt:
        firstNonEmptyString(
          sourceObj?.parsed_at,
          sourceObj?.parsedAt,
          sourceObj?.updated_at,
          sourceObj?.created_at,
          sourceObj?.extracted_at
        ) || null
    },
    parsed,
    fields
  };
};

export const buildFormDataSummary = (payloadOrFields: unknown): FormDataSummary => {
  const fields = Array.isArray(payloadOrFields)
    ? (payloadOrFields as NormalizedFormField[])
    : normalizeFormPayload(payloadOrFields).fields;
  const totalFields = fields.length;
  const filledFields = fields.filter((field) => firstNonEmptyString(field.value)).length;
  const contextualFields = fields.filter((field) => firstNonEmptyString(field.context_id)).length;
  const keyedFields = fields.filter((field) => firstNonEmptyString(field.field_key)).length;
  const checkboxFields = fields.filter((field) => field.is_checkbox_option).length;
  const checkedCheckboxes = fields.filter((field) => field.is_checkbox_option && field.is_checked === true).length;
  const confidenceScores = fields
    .map((field) => normalizeConfidence(field.confidence))
    .filter((value): value is number => value !== null);
  const highConfidenceCount = confidenceScores.filter((score) => score >= 70).length;
  const sectionsCount = new Set(fields.map((field) => field.group)).size;
  const missingFields = Math.max(0, totalFields - filledFields);

  return {
    totalFields,
    filledFields,
    missingFields,
    coverage: totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0,
    sectionsCount,
    contextualFields,
    contextualCoverage: totalFields > 0 ? Math.round((contextualFields / totalFields) * 100) : 0,
    keyedFields,
    keyedCoverage: totalFields > 0 ? Math.round((keyedFields / totalFields) * 100) : 0,
    checkboxFields,
    checkedCheckboxes,
    avgConfidence:
      confidenceScores.length > 0
        ? Math.round(confidenceScores.reduce((sum, score) => sum + score, 0) / confidenceScores.length)
        : null,
    highConfidenceRate:
      confidenceScores.length > 0 ? Math.round((highConfidenceCount / confidenceScores.length) * 100) : null
  };
};

export const hasApplicationFormData = (value: unknown): boolean => {
  if (!value) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;

  if (typeof value === 'object') {
    const obj = value as GenericRecord;
    if (obj.has_application_form === true) return true;

    if (Array.isArray(obj.documents)) {
      const hasFormDoc = obj.documents.some((doc) => {
        if (!doc || typeof doc !== 'object') return false;
        const d = doc as GenericRecord;
        const role = toSearchText(d.role);
        const title = [d.title, d.name, d.description, d.doc_type, d.document_type, d.category]
          .map(toSearchText)
          .join(' ');
        return role === 'application_form' || /application form/.test(title);
      });
      if (hasFormDoc) return true;
    }

    const inline = extractInlineApplicationFormData(obj);
    if (inline !== null && inline !== undefined) {
      if (typeof inline === 'string') return inline.trim().length > 0;
      if (Array.isArray(inline)) return inline.length > 0;
      if (typeof inline === 'object') return Object.keys(inline as GenericRecord).length > 0;
    }

    return false;
  }

  return false;
};
